import React, { Children, cloneElement, useEffect, useMemo, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';

const DockStyles = () => (
  <style>{`
    .dock-container {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      pointer-events: none;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding-bottom: 1.5rem;
      z-index: 50;
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
    }

    .dock-outer {
      margin: 0 0.5rem;
      display: flex;
      max-width: 100%;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
    }

    .dock-panel {
      display: flex;
      align-items: flex-end;
      width: fit-content;
      gap: 0.5rem;
      border-radius: 24px;
      background: rgba(10, 10, 12, 0.85);
      backdrop-filter: blur(32px) saturate(200%);
      border: 1px solid rgba(201, 168, 76, 0.3); /* Gold border for Templar theme */
      padding: 0.5rem;
      box-shadow: 
        0 20px 50px -10px rgba(0, 0, 0, 0.7),
        inset 0 1px 1px rgba(255, 255, 255, 0.03);
    }

    .dock-item {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.05);
      cursor: pointer;
      outline: none;
      color: #a1a1aa;
      transition: color 0.2s, background 0.3s, border-color 0.3s, border-radius 0.2s;
      overflow: hidden;
    }

    .dock-item::before {
      content: '';
      position: absolute;
      bottom: -15%;
      left: 50%;
      transform: translateX(-50%);
      width: 110%;
      height: 50%;
      background: radial-gradient(
        circle at center, 
        rgba(201, 168, 76, 0.15) 0%, /* Gold glow */
        transparent 75%
      );
      pointer-events: none;
      z-index: 1;
      opacity: 0.6;
      transition: opacity 0.3s;
    }

    .dock-item:hover {
      background: rgba(201, 168, 76, 0.05); /* Gold hover bg */
      color: #c9a84c; /* Gold text */
      border-color: rgba(201, 168, 76, 0.3);
    }

    .dock-item:hover::before {
      opacity: 1;
      background: radial-gradient(
        circle at center, 
        rgba(201, 168, 76, 0.25) 0%, 
        transparent 70%
      );
    }

    .dock-separator {
      width: 1px;
      height: 24px;
      background: rgba(255, 255, 255, 0.08);
      margin: 0 0.3rem 0.6rem;
      align-self: flex-end;
    }

    .dock-indicator {
      position: absolute;
      bottom: 4px;
      width: 4px;
      height: 4px;
      background: #c9a84c;
      border-radius: 50%;
      box-shadow: 0 0 10px 1px rgba(201, 168, 76, 0.8);
      z-index: 5;
    }

    .dock-icon {
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dock-label {
      position: absolute;
      top: -3.2rem;
      left: 50%;
      white-space: pre;
      border-radius: 0.6rem;
      background: rgba(10, 10, 10, 0.85);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(201, 168, 76, 0.3);
      padding: 0.4rem 0.9rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: #c9a84c;
      transform: translateX(-50%);
      pointer-events: none;
      box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.6);
    }
  `}</style>
);

function DockItem({ children, className = '', onClick, mouseX, spring, distance, magnification, baseItemSize, isActive }: any) {
  const ref = useRef<HTMLDivElement>(null);
  const isHovered = useMotionValue(0);

  const mouseDistance = useTransform(mouseX, (val: number) => {
    const rect = ref.current?.getBoundingClientRect() ?? {
      x: 0,
      width: baseItemSize
    };
    return val - rect.x - baseItemSize / 2;
  });

  const targetSize = useTransform(mouseDistance, [-distance, 0, distance], [baseItemSize, magnification, baseItemSize]);
  const size = useSpring(targetSize, spring);

  return (
    <motion.div
      ref={ref}
      style={{ width: size, height: size }}
      onHoverStart={() => isHovered.set(1)}
      onHoverEnd={() => isHovered.set(0)}
      onClick={onClick}
      className={`dock-item ${className}`}
      role="button"
      tabIndex={0}
    >
      {Children.map(children, child => cloneElement(child as any, { isHovered }))}
      {isActive && <div className="dock-indicator" />}
    </motion.div>
  );
}

function DockLabel({ children, ...rest }: any) {
  const { isHovered } = rest;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const unsubscribe = isHovered.on('change', (latest: number) => setIsVisible(latest === 1));
    return () => unsubscribe();
  }, [isHovered]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          className="dock-label"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const DockIcon = ({ children }: any) => <div className="dock-icon">{children}</div>;

export function Dock({
  items,
  spring = { mass: 0.1, stiffness: 220, damping: 18 },
  magnification = 64,
  distance = 140,
  panelHeight = 64,
  baseItemSize = 48
}: any) {
  const mouseX = useMotionValue(Infinity);

  const handleMouseMove = (e: React.MouseEvent) => {
    mouseX.set(e.pageX);
  };

  return (
    <div className="dock-container" onMouseMove={handleMouseMove} onMouseLeave={() => mouseX.set(Infinity)}>
      <DockStyles />
      <div className="dock-outer">
        <div className="dock-panel" style={{ height: panelHeight }}>
          {items.map((item: any, index: number) => (
            <React.Fragment key={index}>
              {item.type === 'separator' ? (
                <div className="dock-separator" />
              ) : (
                <DockItem
                  onClick={() => {
                    if(item.onClick) item.onClick();
                  }}
                  isActive={item.isActive}
                  mouseX={mouseX}
                  spring={spring}
                  distance={distance}
                  magnification={magnification}
                  baseItemSize={baseItemSize}
                >
                  <DockIcon>{item.icon}</DockIcon>
                  <DockLabel>{item.label}</DockLabel>
                </DockItem>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
